/**
 * Helpers for keeping soft-deleted ("removed") projects and cost centres
 * available to a form that already references them. The standard lookups
 * exclude deleted records, so an existing booking would otherwise lose its
 * Project / Cost Centre selection.
 */

interface HasId {
  id: string | number;
}

/** Appends a removed record to a lookup list unless it is already present. */
export function withRemovedRecord<T extends HasId>(records: T[], removed: T | null | undefined): T[] {
  if (!removed) return records;
  if (records.some(r => String(r.id) === String(removed.id))) return records;
  return [...records, removed];
}

/** Appends removed records to a lookup list, skipping ones already present. */
export function withRemovedRecords<T extends HasId>(records: T[], removed: readonly T[]): T[] {
  if (removed.length === 0) return records;
  const knownIds = new Set(records.map(r => String(r.id)));
  return [...records, ...removed.filter(r => !knownIds.has(String(r.id)))];
}

/**
 * True when a booking references a project that is not present in the
 * (deletion-filtered) lookup list, i.e. the project has been removed.
 */
export function isProjectMissingFromLookup<T extends HasId>(
  projects: T[],
  bookingProjectId: string | number | null | undefined,
): boolean {
  if (bookingProjectId == null || bookingProjectId === '') return false;
  if (projects.length === 0) return false;
  return !projects.some(p => String(p.id) === String(bookingProjectId));
}
